import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../contexts/AuthContext";
import { formatDate } from "../../../utils/DateFormat";
import { customerProfileService } from "../../../services/api/customerProfileService";
import { contractService } from "../../../services/api/contractService";
import { StatusBadge } from "../../../constants/statusConstants";
import Loading from "../components/Loading";
import CustomTable from "../components/CustomTable";
import SearchAndFilterBar from "../components/SearchAndFilterBar";
import Modal from "../components/Modal";
import {
  buildDocumentManagerState,
  withDocumentManagerSearch,
} from "../../../utils/navigationContext";

const columns = [
  {
    key: "id",
    title: "STT",
    minWidth: "64px",
    align: "text-center",
    render: (value) => <div>{value}</div>,
  },
  {
    key: "mau",
    title: "Mẫu",
    minWidth: "112px",
    align: "text-center",
    render: (value) => (
      <div className="flex items-center justify-center">
        <img
          src={value}
          alt="Hình mẫu"
          className="size-20 rounded-md object-contain ring-1 ring-slate-100"
        />
      </div>
    ),
  },
  { key: "ten", title: "Tên", minWidth: "220px" },
  {
    key: "soDon",
    title: "Số đơn",
    minWidth: "160px",
    color: "text-[#0954E9]",
  },
  { key: "ngayNopHoSo", title: "Ngày nộp đơn", minWidth: "150px" },
  { key: "phanLoai", title: "Phân loại", minWidth: "120px" },
  {
    key: "chuSoHuu",
    title: "Chủ đơn",
    minWidth: "200px",
  },
  {
    key: "trangThai",
    title: "Trạng thái",
    minWidth: "160px",
    align: "text-center",
    render: (value) => <StatusBadge status={value} />,
  },
];

const DocumentManager = () => {
  const { token, user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const [inputModal, setInputModal] = useState({
    isOpen: false,
    title: "",
    placeholder: "",
    onConfirm: null,
  });
  const [inputValue, setInputValue] = useState("");

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [fromDate, setFromDate] = useState(searchParams.get("fromDate") || "");
  const [toDate, setToDate] = useState(searchParams.get("toDate") || "");

  const currentPage = Number(searchParams.get("page")) || 1;
  const documentManagerState = useMemo(() => buildDocumentManagerState(), []);

  const navigateToProfileDetail = useCallback(
    (profileId) => {
      if (!profileId) return;

      const detailPath = `/quan-ly-dich-vu-khach-hang/ho-so/${profileId}`;
      navigate(withDocumentManagerSearch(detailPath), {
        state: documentManagerState,
      });
    },
    [documentManagerState, navigate],
  );

  const navigateToProfileEdit = useCallback(
    (profileId) => {
      if (!profileId) return;

      const editPath = `/quan-ly-dich-vu-khach-hang/ho-so/${profileId}/chinh-sua`;
      navigate(withDocumentManagerSearch(editPath), {
        state: documentManagerState,
      });
    },
    [documentManagerState, navigate],
  );

  const formatData = useCallback(
    (apiResponse) => {
      return apiResponse.map((item, index) => ({
        id: itemsPerPage * (currentPage - 1) + index + 1,
        hoSo: item.serviceId?.serviceCode || "",
        ten: item.brand || "",
        mau: item.logo[0]?.isImage
          ? item.logo[0]?.value
          : item.image?.url || "/images/default-profile.png",
        soDon: item.profileCode || "--",
        ngayNopHoSo: formatDate(item.createdDate),
        phanLoai: item.groupNames?.join("; ") || "--",
        soBang: item.numberOfCertificates || "--",
        hieuLuc: formatDate(item.dateActive),
        chuSoHuu: item.owner[0] || "--",
        trangThai: item.status || "Chưa có trạng thái",
        nav_id: item._id,
        duplicate_id: item._id,
      }));
    },
    [currentPage, itemsPerPage],
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await customerProfileService.get_all_profile(
        currentPage,
        itemsPerPage,
        token,
        searchParams.get("search") || "",
        searchParams.get("fromDate") || "",
        searchParams.get("toDate") || "",
      );
      setData(formatData(response.data));
      setTotalPages(
        response.pagination?.totalProfiles === 0
          ? 1
          : response.pagination?.totalPages || 1,
      );
    } catch (error) {
      toast.dismiss();
      toast.error(
        error.response?.data?.message || "Không thể kết nối tới server.",
      );
    } finally {
      setLoading(false);
    }
  }, [currentPage, token, searchParams, formatData, itemsPerPage]);

  const handleSearchApply = () => {
    const newParams = new URLSearchParams();
    if (search) newParams.set("search", search);
    if (fromDate) newParams.set("fromDate", fromDate);
    if (toDate) newParams.set("toDate", toDate);
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const handleDelete = async (id) => {
    setConfirmModal({
      isOpen: true,
      title: "Xác nhận xóa",
      message: "Bạn có chắc muốn xóa hồ sơ này không?",
      onConfirm: async () => {
        try {
          setLoading(true);
          closeConfirmModal();

          const res = await customerProfileService.delete(id, token);
          toast.success(res?.message || "Đã xóa hồ sơ thành công");
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.message || "Lỗi khi xóa");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleDuplicate = async (id) => {
    try {
      setLoading(true);
      const res = await customerProfileService.duplicate_profile(id, token);
      navigateToProfileEdit(res.profile._id);
      toast.success("Đã nhân bản hồ sơ thành công");
    } catch (error) {
      toast.error(error.response?.data?.message || "Lỗi khi nhân bản");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestApprove = async (item) => {
    setInputModal({
      isOpen: true,
      title: "Đề xuất duyệt hồ sơ",
      placeholder: "Nhập nội dung đề xuất duyệt...",
      onConfirm: async (message) => {
        try {
          setLoading(true);
          closeInputModal();

          await contractService.request_profile(token, item.nav_id, {
            note: message,
          });

          toast.success("Đã gửi đề xuất duyệt hồ sơ");
          fetchData();
        } catch (error) {
          toast.error(
            error.response?.data?.message || "Không thể gửi đề xuất duyệt",
          );
        } finally {
          setLoading(false);
        }
      },
    });
    setInputValue("");
  };

  useEffect(() => {
    fetchData();
    if (user?.role === "SuperAdmin") setCanDelete(true);
  }, [fetchData, user?.role]);

  const handleFromDateChange = (newFromDate) => {
    if (!toDate) {
      setFromDate(newFromDate);
      return;
    }
    const to = toDate ? new Date(toDate.split("-").reverse().join("-")) : null;
    const from = new Date(newFromDate.split("-").reverse().join("-"));

    if (to && from > to) {
      alert("⚠️ Ngày bắt đầu không được lớn hơn ngày kết thúc.");
      return;
    }

    setFromDate(newFromDate);
  };

  const handleToDateChange = (newToDate) => {
    if (!fromDate) {
      setToDate(newToDate);
      return;
    }

    const from = fromDate
      ? new Date(fromDate.split("-").reverse().join("-"))
      : null;
    const to = new Date(newToDate.split("-").reverse().join("-"));

    if (from && to < from) {
      alert("⚠️ Ngày kết thúc không được nhỏ hơn ngày bắt đầu.");
      return;
    }

    setToDate(newToDate);
  };

  const closeConfirmModal = () => {
    setConfirmModal({ isOpen: false, title: "", message: "", onConfirm: null });
  };

  const closeInputModal = () => {
    setInputModal({
      isOpen: false,
      title: "",
      placeholder: "",
      onConfirm: null,
    });
    setInputValue("");
  };

  const handleInputConfirm = () => {
    if (!inputValue.trim()) {
      toast.warning("Vui lòng nhập nội dung");
      return;
    }
    if (inputModal.onConfirm) {
      inputModal.onConfirm(inputValue);
    }
  };

  return (
    <div className="flex flex-col h-full gap-4">
      <div className="flex-shrink-0">
        <SearchAndFilterBar
          searchPlaceholder="Tìm kiếm hồ sơ"
          searchValue={search}
          onSearchChange={(e) => setSearch(e.target.value)}
          onSearchClick={handleSearchApply}
          showDatePickers
          showFilter
          filterLabel="Áp dụng"
          fromDatePlaceholder="Từ ngày"
          fromDateValue={fromDate}
          toDatePlaceholder="Đến ngày"
          toDateValue={toDate}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDateChange}
          showAdditionBtn={false}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <CustomTable
          data={data}
          columns={columns}
          currentPage={currentPage}
          setCurrentPage={(page) => {
            setSearchParams((prev) => {
              const params = new URLSearchParams(prev);
              params.set("page", page.toString());
              return params;
            });
          }}
          totalPages={totalPages}
          setView={(item) => navigateToProfileDetail(item.nav_id)}
          setEdit={false}
          setDuplicate
          setDelete={canDelete}
          handleDelete={handleDelete}
          handleDuplicate={handleDuplicate}
          onRowClick={(item) => navigateToProfileDetail(item.nav_id)}
          basePath="/quan-ly-dich-vu-khach-hang/ho-so"
          loading={loading}
          loadingText="Đang tải hồ sơ..."
          extraActions={(item) => {
            if (item.trangThai !== "Chờ duyệt") return [];

            return [
              {
                icon: "/icons/up-align.svg",
                label: "Đề xuất duyệt",
                title: "Đề xuất duyệt hồ sơ",
                color: "text-blue-600",
                onClick: (e) => {
                  e.stopPropagation();
                  handleRequestApprove(item);
                },
              },
            ];
          }}
        />
      </div>

      <Modal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        title={confirmModal.title}
        maxWidth="500px"
        primaryButtonText="Đồng ý"
        primaryButtonOnClick={confirmModal.onConfirm}
        cancelButtonText="Hủy"
      >
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600 whitespace-pre-line">
            {confirmModal.message}
          </p>
        </div>
      </Modal>

      <Modal
        isOpen={inputModal.isOpen}
        onClose={closeInputModal}
        title={inputModal.title}
        maxWidth="500px"
        primaryButtonText="Đồng ý"
        primaryButtonOnClick={handleInputConfirm}
        cancelButtonText="Hủy"
      >
        <div className="px-6 py-4">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={inputModal.placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={4}
            autoFocus
          />
        </div>
      </Modal>
    </div>
  );
};

export default DocumentManager;
